/**
 * Mission document helpers — upload, saveMission image, and link via addMissionAction.
 */

import { uploadFile } from "services/fileUpload";
import {
  addMissionAction,
  addResourceRequestAction,
  getFile,
  getResourceRequestById,
  getResourceRequestDocuments,
  getTimeline,
  saveMission,
  saveResourceRequest,
} from "layouts/dashboardLayout/pages/Users/services/graphqlService";

export const DOCUMENT_LINK_ACTION = {
  message: "Document",
  type: "Document",
  subType: "documentUpload",
};

export const MISSION_DOCUMENT_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,image/jpeg,image/jpg,image/png,image/gif,image/webp";

/** @deprecated Use MISSION_DOCUMENT_ACCEPT */
export const DOCUMENT_ACCEPT = MISSION_DOCUMENT_ACCEPT;

/**
 * @typedef {{ name: string, key: string }} MissionDocumentEntry
 */

/**
 * S3 key / path for documentPath or documents[].key
 */
export function buildDocumentPathFromUpload(uploaded) {
  if (!uploaded || typeof uploaded !== "object") return "";
  const fileKey = uploaded.fileKey ? String(uploaded.fileKey).trim() : "";
  const fileName = (uploaded.fileName || "").toString().trim();
  return fileKey || fileName;
}

/**
 * Parse documents JSON from action data value (array or stringified JSON).
 */
export function parseDocumentsDataValue(value) {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  const trimmed = value.trim();
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    try {
      const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}

/**
 * actionInput linking multiple documents in one action.
 *
 * @param {MissionDocumentEntry[]} documents
 */
export function buildDocumentsLinkActionInput(documents) {
  const list = (documents || [])
    .map((d) => ({
      name: String(d?.name ?? "").trim(),
      key: String(d?.key ?? d?.path ?? "").trim(),
    }))
    .filter((d) => d.name && d.key);

  if (!list.length) {
    throw new Error("At least one document with name and key is required");
  }

  return {
    message: DOCUMENT_LINK_ACTION.message,
    type: DOCUMENT_LINK_ACTION.type,
    subType: DOCUMENT_LINK_ACTION.subType,
    data: [
      {
        name: "documents",
        value: JSON.stringify(list),
      },
    ],
  };
}

/** @deprecated Use buildDocumentsLinkActionInput */
export function buildDocumentLinkActionInput(documentName, documentPath) {
  return buildDocumentsLinkActionInput([
    { name: documentName, key: documentPath },
  ]);
}

/**
 * Upload files; returns entries ready for link action.
 *
 * @param {File[]} files
 * @param {string} idToken
 * @param {{ onFileProgress?: (fileName: string, percent: number, index: number, total: number) => void }} [options]
 * @returns {Promise<MissionDocumentEntry[]>}
 */
export async function uploadMissionFiles(files, idToken, options = {}) {
  const { onFileProgress } = options;
  if (!files?.length) return [];
  if (!idToken) throw new Error("Authentication is required to upload documents");

  const documents = [];
  const total = files.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const uploaded = await uploadFile(file, idToken, (percent) =>
      onFileProgress?.(file.name, percent, i, total),
    );
    const key = buildDocumentPathFromUpload(uploaded);
    if (!key) {
      throw new Error(
        `Upload succeeded but file path was missing for ${file.name}`,
      );
    }
    documents.push({
      name: file.name || uploaded.originalName || "Document",
      key,
    });
  }

  return documents;
}

/**
 * Link multiple uploaded documents to a mission (single addMissionAction).
 */
export async function linkMissionDocuments(missionId, documents, idToken) {
  if (!missionId) throw new Error("missionId is required");
  if (!idToken) throw new Error("Authentication is required");
  const input = buildDocumentsLinkActionInput(documents);
  return addMissionAction(missionId, input, idToken);
}

/** @deprecated Use linkMissionDocuments */
export async function linkMissionDocument(
  missionId,
  documentName,
  documentPath,
  idToken,
) {
  return linkMissionDocuments(
    missionId,
    [{ name: documentName, key: documentPath }],
    idToken,
  );
}

/**
 * Upload files then link them in one mission action.
 */
export async function uploadAndLinkMissionDocuments(
  missionId,
  files,
  idToken,
  options = {},
) {
  const { onFileProgress, onLinking, getDocumentName } = options;
  if (!files?.length) throw new Error("No files selected");
  if (!missionId) throw new Error("missionId is required");
  if (!idToken) throw new Error("Authentication is required");

  const uploaded = await uploadMissionFiles(files, idToken, { onFileProgress });
  const documents = uploaded.map((doc, i) => ({
    name:
      (typeof getDocumentName === "function"
        ? getDocumentName(files[i], doc)
        : null) ||
      doc.name,
    key: doc.key,
  }));

  if (onLinking) onLinking();
  const actionResult = await linkMissionDocuments(
    missionId,
    documents,
    idToken,
  );

  return { documents, actionResult };
}

/**
 * Link uploaded documents to a resource request (single addResourceRequestAction).
 */
export async function linkResourceRequestDocuments(requestId, documents, idToken) {
  if (!requestId) throw new Error("requestId is required");
  if (!idToken) throw new Error("Authentication is required");
  const input = buildDocumentsLinkActionInput(documents);
  return addResourceRequestAction(requestId, input, idToken);
}

/**
 * Upload files then link them to a resource request.
 */
export async function uploadAndLinkResourceRequestDocuments(
  requestId,
  files,
  idToken,
  options = {},
) {
  const { onFileProgress, onLinking, getDocumentName } = options;
  if (!files?.length) throw new Error("No files selected");
  if (!requestId) throw new Error("requestId is required");
  if (!idToken) throw new Error("Authentication is required");

  const uploaded = await uploadMissionFiles(files, idToken, { onFileProgress });
  const documents = uploaded.map((doc, i) => ({
    name:
      (typeof getDocumentName === "function"
        ? getDocumentName(files[i], doc)
        : null) ||
      doc.name,
    key: doc.key,
  }));

  if (onLinking) onLinking();
  const actionResult = await linkResourceRequestDocuments(
    requestId,
    documents,
    idToken,
  );

  return { documents, actionResult };
}

/**
 * Upload one file then link (convenience wrapper).
 */
export async function uploadAndLinkMissionDocument(
  missionId,
  file,
  idToken,
  options = {},
) {
  const { documentName, onUploadProgress, onLinking } = options;
  return uploadAndLinkMissionDocuments(missionId, [file], idToken, {
    onLinking,
    onFileProgress: onUploadProgress
      ? (name, percent) => onUploadProgress(percent)
      : undefined,
    getDocumentName: () =>
      (documentName && String(documentName).trim()) ||
      file.name ||
      "Document",
  });
}

function parseFileMessagePayload(message) {
  if (message == null || message === "") return null;
  if (typeof message === "object") return message;
  try {
    return JSON.parse(message);
  } catch {
    const fromString = extractPresignedUrlFromMessageString(message);
    if (fromString) return { signedUrl: fromString };
    return { raw: message };
  }
}

/**
 * Backend may return Message as `{presigned_url=https://...}` (not JSON).
 */
export function extractPresignedUrlFromMessageString(message) {
  const str = String(message ?? "").trim();
  if (!str) return null;

  const keyed = str.match(/presigned_url\s*[=:]\s*(https?:\/\/.+)/i);
  if (keyed?.[1]) {
    return keyed[1].replace(/}\s*$/, "").trim();
  }

  const httpsIdx = str.indexOf("https://");
  const httpIdx = str.indexOf("http://");
  const start =
    httpsIdx >= 0 && (httpIdx < 0 || httpsIdx <= httpIdx)
      ? httpsIdx
      : httpIdx >= 0
        ? httpIdx
        : -1;
  if (start < 0) return null;

  let url = str.slice(start).trim();
  if (url.endsWith("}")) url = url.slice(0, -1).trim();
  return url || null;
}

/**
 * Extract presigned download URL from getFile fileResponse.
 */
export function extractSignedUrlFromFileResponse(fileResponse) {
  const message = fileResponse?.Message;
  if (message == null || message === "") {
    throw new Error("Failed to get file URL (empty response)");
  }

  const fromString = extractPresignedUrlFromMessageString(message);
  if (fromString) return fromString;

  const parsed = parseFileMessagePayload(message);
  if (!parsed || typeof parsed !== "object") {
    throw new Error(String(message));
  }
  const url =
    parsed.signedUrl ??
    parsed.SignedUrl ??
    parsed.presigned_url ??
    parsed.presignedUrl ??
    parsed.url ??
    parsed.URL ??
    parsed.downloadUrl ??
    parsed.href;
  if (!url) {
    throw new Error(
      parsed.message ||
        parsed.error ||
        parsed.raw ||
        "Download URL was not returned from getFile",
    );
  }
  return String(url);
}

export function resolveMissionDocumentFileRef(doc) {
  const fileName = (
    doc?.key ??
    doc?.documentPath ??
    doc?.fileName ??
    ""
  )
    .toString()
    .trim();
  const bucket = doc?.bucket ? String(doc.bucket).trim() : undefined;
  return { fileName, bucket };
}

/**
 * Presigned URL via getFile for view/download.
 *
 * @param {{ bucket?: string, key?: string, documentPath?: string, fileName?: string }} doc
 * @param {string} idToken
 * @returns {Promise<string>} signed URL
 */
export async function getMissionDocumentSignedUrl(doc, idToken) {
  const { fileName, bucket } = resolveMissionDocumentFileRef(doc);
  if (!fileName) throw new Error("Document file path is missing");
  if (!idToken) throw new Error("Authentication is required");

  const fileResponse = await getFile(fileName, bucket, idToken);
  return extractSignedUrlFromFileResponse(fileResponse);
}

/** @deprecated Prefer getMissionDocumentSignedUrl + viewer modal */
export async function openMissionDocumentInNewTab(doc, idToken) {
  const signedUrl = await getMissionDocumentSignedUrl(doc, idToken);
  window.open(signedUrl, "_blank", "noopener,noreferrer");
  return signedUrl;
}

/**
 * Trigger browser download using presigned URL.
 */
export async function downloadMissionDocument(doc, idToken) {
  const signedUrl = await getMissionDocumentSignedUrl(doc, idToken);
  const fileName =
    (doc?.documentName || doc?.name || "document").toString().trim() ||
    "document";

  try {
    const res = await fetch(signedUrl);
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = fileName;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    const anchor = document.createElement("a");
    anchor.href = signedUrl;
    anchor.download = fileName;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
}

function prettifyDataKey(key) {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Extra rows for document cards (User, Active, message, refId, etc.).
 * @returns {Array<{ label: string, value: string }>}
 */
export function getDocumentDisplayFields(doc) {
  if (!doc || typeof doc !== "object") return [];

  const rows = [];
  const seen = new Set();
  const add = (label, value) => {
    const v = value == null ? "" : String(value).trim();
    if (!v || seen.has(`${label}:${v}`)) return;
    seen.add(`${label}:${v}`);
    rows.push({ label, value: v });
  };

  add("Uploaded by", doc.loggedProfileName ?? doc.uploadedBy);
  add("Note", doc.message);
  add("Reference", doc.refId);
  if (doc.type && String(doc.type).toLowerCase() !== "document") {
    add("Type", doc.type);
  }
  if (Array.isArray(doc.roles) && doc.roles.length) {
    add("Role", doc.roles.join(", "));
  }
  if (Array.isArray(doc.entityTypes) && doc.entityTypes.length) {
    add("Entity", doc.entityTypes.join(", "));
  }

  const data = doc.data ?? doc.Data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    Object.entries(data).forEach(([key, val]) => {
      if (key === "Documents") return;
      if (val == null || val === "") return;
      if (typeof val === "object") {
        if (Array.isArray(val) && val.length === 0) return;
        if (!Array.isArray(val) && Object.keys(val).length === 0) return;
        add(prettifyDataKey(key), JSON.stringify(val));
        return;
      }
      add(prettifyDataKey(key), val);
    });
  }

  const path = doc.documentPath || doc.key;
  if (path) add("File", path);

  return rows;
}

export function inferDocumentCategoryLabel(documentName, explicitCategory) {
  const cat = (explicitCategory ?? "").toString().trim();
  if (cat && cat.toLowerCase() !== "document") return cat;
  const name = (documentName ?? "").toString().trim();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return parts[0];
  return "Document";
}

export function guessViewerKind(fileName) {
  const lower = (fileName || "").toLowerCase();
  if (/\.(pdf)$/.test(lower) || lower.includes(".pdf")) return "pdf";
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower)) return "image";
  if (/\.(mp4|webm|ogg)$/.test(lower)) return "video";
  return "other";
}

export function guessViewerKindFromMime(mimeType) {
  const mime = (mimeType || "").toLowerCase().split(";")[0].trim();
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return "other";
}

/**
 * URL suitable for in-app preview. Fetches as blob so S3
 * Content-Disposition: attachment does not force download in iframe (PDFs).
 *
 * @returns {Promise<{ previewUrl: string, revoke: () => void, contentType: string }>}
 */
export async function getMissionDocumentPreviewUrl(doc, idToken) {
  const signedUrl = await getMissionDocumentSignedUrl(doc, idToken);
  const pathKind = guessViewerKind(
    doc?.documentPath || doc?.key || doc?.documentName || doc?.name,
  );

  // Images/videos can use presigned URL directly when extension is known.
  if (pathKind === "image" || pathKind === "video") {
    return {
      previewUrl: signedUrl,
      revoke: () => {},
      contentType: pathKind === "image" ? "image/*" : "video/*",
    };
  }

  const res = await fetch(signedUrl);
  if (!res.ok) {
    throw new Error("Failed to load document for preview");
  }

  const headerType = (res.headers.get("content-type") || "")
    .split(";")[0]
    .trim();
  const buffer = await res.arrayBuffer();

  const isPdf =
    pathKind === "pdf" ||
    headerType === "application/pdf" ||
    headerType.includes("pdf");

  const mime = isPdf
    ? "application/pdf"
    : headerType || "application/octet-stream";

  const blob = new Blob([buffer], { type: mime });
  const previewUrl = URL.createObjectURL(blob);

  return {
    previewUrl,
    revoke: () => URL.revokeObjectURL(previewUrl),
    contentType: mime,
  };
}

/**
 * Normalize one document record from getMissionDocuments items or timeline.
 *
 * @returns {{ id: string, documentName: string, documentPath: string, bucket?: string, missionId?: string, documentId?: string, createdAt?: string }|null}
 */
export function normalizeMissionDocumentItem(item, index = 0) {
  if (item == null) return null;
  if (typeof item === "string") {
    return {
      id: `doc-${index}`,
      documentName: item,
      documentPath: "",
    };
  }
  if (typeof item !== "object") return null;

  const documentName = String(
    item.name ??
      item.Name ??
      item.documentName ??
      item.fileName ??
      item.FileName ??
      item.title ??
      "Document",
  ).trim();
  const category = inferDocumentCategoryLabel(
    documentName,
    item.category ??
      item.documentCategory ??
      item.Category ??
      item.type ??
      item.Type,
  );
  const documentPath = String(
    item.key ??
      item.Key ??
      item.documentPath ??
      item.path ??
      item.Path ??
      item.fileKey ??
      item.FileKey ??
      "",
  ).trim();

  if (!documentName && !documentPath) return null;

  const bucket = item.bucket ?? item.Bucket;
  const missionId = item.missionId ?? item.MissionId;
  const rawData = item.data ?? item.Data;

  const normalized = {
    id: String(
      item.documentId ??
        item.id ??
        item.fileId ??
        item.timelineId ??
        (documentPath || `${documentName}-${index}`),
    ),
    documentName: documentName || "Document",
    category,
    documentPath,
    key: documentPath,
    ...(bucket ? { bucket: String(bucket).trim() } : {}),
    ...(missionId ? { missionId: String(missionId).trim() } : {}),
    ...(item.documentId
      ? { documentId: String(item.documentId).trim() }
      : {}),
    ...(item.createdAt || item.timestamp || item.CreatedAt
      ? { createdAt: item.createdAt ?? item.timestamp ?? item.CreatedAt }
      : {}),
    ...(item.message ? { message: String(item.message).trim() } : {}),
    ...(item.loggedProfileName
      ? { loggedProfileName: String(item.loggedProfileName).trim() }
      : {}),
    ...(item.refId != null ? { refId: String(item.refId).trim() } : {}),
    ...(Array.isArray(item.roles) ? { roles: item.roles } : {}),
    ...(Array.isArray(item.entityTypes) ? { entityTypes: item.entityTypes } : {}),
    ...(item.type && String(item.type).toLowerCase() !== "documentupload"
      ? { type: String(item.type).trim() }
      : {}),
    ...(rawData && typeof rawData === "object" ? { data: rawData } : {}),
  };

  normalized.displayFields = getDocumentDisplayFields(normalized);
  return normalized;
}

/**
 * Parse getMissionDocuments AWSJSON: { items: [...], nextToken, ... }.
 *
 * @param {*} data - Parsed or stringified API payload
 * @returns {Array<{ id: string, documentName: string, documentPath: string, bucket?: string, key?: string }>}
 */
export function parseMissionDocumentsResponse(data) {
  if (data == null) return [];

  let raw = data;
  while (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  if (raw?.data != null) {
    let inner = raw.data;
    while (typeof inner === "string") {
      try {
        inner = JSON.parse(inner);
      } catch {
        break;
      }
    }
    raw = inner;
  }

  let list = null;
  if (Array.isArray(raw)) list = raw;
  else if (Array.isArray(raw?.items)) list = raw.items;
  else if (Array.isArray(raw?.Items)) list = raw.Items;
  else if (Array.isArray(raw?.documents)) list = raw.documents;
  else if (Array.isArray(raw?.Documents)) list = raw.Documents;
  else if (Array.isArray(raw?.files)) list = raw.files;
  else if (Array.isArray(raw?.Files)) list = raw.Files;
  else if (Array.isArray(raw?.missionDocuments)) list = raw.missionDocuments;
  else if (Array.isArray(raw?.MissionDocuments)) list = raw.MissionDocuments;
  else if (Array.isArray(raw?.pilotDocuments)) list = raw.pilotDocuments;
  else if (Array.isArray(raw?.resourceRequestDocuments)) {
    list = raw.resourceRequestDocuments;
  } else if (Array.isArray(raw?.ResourceRequestDocuments)) {
    list = raw.ResourceRequestDocuments;
  } else if (Array.isArray(raw?.requestDocuments)) {
    list = raw.requestDocuments;
  }

  if (!list) return [];

  return list
    .map((item, index) => normalizeMissionDocumentItem(item, index))
    .filter(Boolean);
}

function parseTimelineEventsPayload(data) {
  if (data == null) return [];
  let raw = data;
  while (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.Items)) return raw.Items;
  if (Array.isArray(raw?.timeline)) return raw.timeline;
  if (Array.isArray(raw?.Timeline)) return raw.Timeline;
  if (Array.isArray(raw?.events)) return raw.events;
  return [];
}

/**
 * Extract documents embedded in a resource request payload.
 *
 * @param {*} data - getResourceRequestId AWSJSON (parsed or string)
 */
export function parseResourceRequestDocumentsFromPayload(data) {
  if (data == null) return [];

  let parsed = data;
  while (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }

  if (parsed && typeof parsed === "object" && !parsed.requestId && !parsed.title) {
    const list =
      parsed.ResourceRequests ||
      parsed.ResourceRequestsList ||
      parsed.items ||
      (Array.isArray(parsed) ? parsed : null);
    if (Array.isArray(list) && list.length > 0) {
      parsed = list[0];
    }
  }

  const direct =
    parsed?.documents ??
    parsed?.Documents ??
    parsed?.documentList ??
    parsed?.linkedDocuments ??
    parsed?.resourceRequestDocuments ??
    parsed?.ResourceRequestDocuments;

  if (direct != null) {
    const docs = parseMissionDocumentsResponse(
      Array.isArray(direct) ? { items: direct } : direct,
    );
    if (docs.length) return docs;
  }

  const timelineEvents =
    parsed?.timeline ??
    parsed?.Timeline ??
    parsed?.events ??
    parsed?.actions ??
    parsed?.Actions;
  if (timelineEvents) {
    const fromTimeline = parseLinkedDocumentsFromTimeline(
      parseTimelineEventsPayload(timelineEvents),
    );
    if (fromTimeline.length) return fromTimeline;
  }

  return parseMissionDocumentsResponse(parsed);
}

/**
 * Load documents for a resource request (dedicated query, request payload, timeline).
 *
 * @param {string} requestId
 * @param {string} idToken
 * @param {*} [initialPayload] - Already-loaded request record
 */
export async function loadResourceRequestDocuments(
  requestId,
  idToken,
  initialPayload,
) {
  if (!requestId || !idToken) return [];

  const fromInitial = parseResourceRequestDocumentsFromPayload(initialPayload);
  if (fromInitial.length) return fromInitial;

  try {
    const res = await getResourceRequestDocuments(requestId, idToken);
    const docs = parseMissionDocumentsResponse(res?.data ?? res?.raw);
    if (docs.length) return docs;
  } catch {
    // Query may not exist on backend yet.
  }

  try {
    const res = await getResourceRequestById(requestId, idToken);
    const docs = parseResourceRequestDocumentsFromPayload(res?.data);
    if (docs.length) return docs;
  } catch {
    // Fall through to timeline.
  }

  try {
    const res = await getTimeline(String(requestId), idToken);
    const events = parseTimelineEventsPayload(res?.data);
    const docs = parseLinkedDocumentsFromTimeline(events);
    if (docs.length) return docs;
  } catch {
    // No timeline for this request.
  }

  return [];
}

/**
 * Parse document entries from raw timeline API events.
 */
export function parseLinkedDocumentsFromTimeline(events) {
  if (!Array.isArray(events)) return [];

  const out = [];

  events
    .filter((ev) => (ev?.subType ?? "").toLowerCase() === "documentupload")
    .forEach((ev) => {
      const rows = ev.data ?? ev.Data ?? ev.payload?.data;
      let parsedBatch = false;

      if (Array.isArray(rows)) {
        rows.forEach((row) => {
          const n = row?.name ?? row?.Name;
          const v = row?.value ?? row?.Value;
          if (n !== "documents") return;
          parsedBatch = true;
          const list = parseDocumentsDataValue(v);
          list.forEach((item) => {
            const documentName =
              item?.name ?? item?.Name ?? item?.documentName ?? "Document";
            const documentPath =
              item?.key ?? item?.Key ?? item?.path ?? item?.documentPath ?? "";
            if (!documentName && !documentPath) return;
            const entry = {
              timelineId: ev.timelineId ?? ev.id,
              documentName: String(documentName),
              category: inferDocumentCategoryLabel(
                documentName,
                item?.category ?? item?.type ?? ev.type,
              ),
              documentPath: String(documentPath),
              key: String(documentPath),
              createdAt: ev.createdAt ?? ev.timestamp ?? ev.CreatedAt,
              message: ev.message,
              loggedProfileName: ev.loggedProfileName,
              refId: ev.refId,
              roles: ev.roles,
              entityTypes: ev.entityTypes,
              type: ev.type,
              data: ev.data ?? ev.Data,
            };
            entry.displayFields = getDocumentDisplayFields(entry);
            out.push(entry);
          });
        });
      }

      if (!parsedBatch) {
        let documentName = "";
        let documentPath = "";
        if (Array.isArray(rows)) {
          rows.forEach((row) => {
            const n = row?.name ?? row?.Name;
            const v = row?.value ?? row?.Value;
            if (n === "documentName") documentName = String(v ?? "");
            if (n === "documentPath") documentPath = String(v ?? "");
          });
        }
        if (documentName || documentPath) {
          const entry = {
            timelineId: ev.timelineId ?? ev.id,
            documentName: documentName || ev.message || "Document",
            category: inferDocumentCategoryLabel(
              documentName || ev.message,
              ev.type,
            ),
            documentPath,
            key: documentPath,
            createdAt: ev.createdAt ?? ev.timestamp ?? ev.CreatedAt,
            message: ev.message,
            loggedProfileName: ev.loggedProfileName,
            refId: ev.refId,
            roles: ev.roles,
            entityTypes: ev.entityTypes,
            type: ev.type,
            data: ev.data ?? ev.Data,
          };
          entry.displayFields = getDocumentDisplayFields(entry);
          out.push(entry);
        }
      }
    });

  return out;
}

/**
 * Attach timeline metadata (message, data, roles, etc.) when keys match.
 */
export function mergeDocumentsWithTimelineMetadata(documents, timelineEvents) {
  if (!Array.isArray(documents) || documents.length === 0) return documents;
  const fromTimeline = parseLinkedDocumentsFromTimeline(timelineEvents);
  if (!fromTimeline.length) {
    return documents.map((doc) => ({
      ...doc,
      displayFields:
        doc.displayFields ?? getDocumentDisplayFields(doc),
    }));
  }

  return documents.map((doc) => {
    const path = (doc.documentPath || doc.key || "").trim();
    const match = fromTimeline.find((t) => {
      const tPath = (t.documentPath || t.key || "").trim();
      return path && tPath && path === tPath;
    });
    if (!match) {
      return {
        ...doc,
        displayFields: doc.displayFields ?? getDocumentDisplayFields(doc),
      };
    }
    const merged = {
      ...match,
      ...doc,
      documentName: doc.documentName || match.documentName,
      documentPath: doc.documentPath || match.documentPath,
      key: doc.key || match.key,
      createdAt: doc.createdAt || match.createdAt,
    };
    merged.displayFields = getDocumentDisplayFields(merged);
    return merged;
  });
}

/**
 * @param {object|null|undefined} image
 */
export function parseMissionImageRef(image) {
  if (!image || typeof image !== "object") return null;
  const fileName = (image.fileName || image.fileKey || "").toString().trim();
  if (!fileName) return null;
  const bucket = image.bucket ? String(image.bucket).trim() : "";
  return {
    fileName,
    ...(bucket ? { bucket } : {}),
    originalName:
      image.originalName || image.fileName || image.fileKey || "Document",
  };
}

/**
 * @deprecated Prefer uploadAndLinkMissionDocuments after save.
 * Sets missionPayload.image to the first uploaded file only.
 */
export async function attachMissionDocuments(missionPayload, options = {}) {
  const { files = [], idToken, onFileProgress } = options;
  if (!files.length) return missionPayload;

  const documents = await uploadMissionFiles(files, idToken, {
    onFileProgress,
  });
  if (!documents.length) return missionPayload;

  return {
    ...missionPayload,
    image: { fileName: documents[0].key },
  };
}

export const MISSION_BANNER_ASPECT = 16 / 5;
export const MISSION_IMAGE_BUCKET = "info.avipair.com";

function clampPan(value, fallback = 0.5) {
  const n = Number(value);
  if (Number.isNaN(n)) return fallback;
  return Math.min(1, Math.max(0, n));
}

/** Accepts 0–1, or `{ x, y }` from drag-to-frame. */
export function normalizeBannerPan(pan) {
  if (pan && typeof pan === "object") {
    return {
      x: clampPan(pan.x),
      y: clampPan(pan.y),
    };
  }
  const t = clampPan(pan);
  return { x: t, y: t };
}

export async function cropImageToBannerFile(file, pan = 0.5) {
  if (!file) return null;
  const img = await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read the selected image"));
    };
    image.src = url;
  });

  const outW = 1920;
  const outH = Math.round(outW / MISSION_BANNER_ASPECT);
  const coverScale = Math.max(
    outW / img.naturalWidth,
    outH / img.naturalHeight,
  );
  const extraX = Math.max(0, img.naturalWidth * coverScale - outW);
  const extraY = Math.max(0, img.naturalHeight * coverScale - outH);
  const { x: panX, y: panY } = normalizeBannerPan(pan);
  const sx = (extraX * panX) / coverScale;
  const sy = (extraY * panY) / coverScale;
  const sw = outW / coverScale;
  const sh = outH / coverScale;

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not crop image");
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Could not crop image"))),
      "image/jpeg",
      0.88,
    );
  });
  const base = String(file.name || "mission-banner").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}-banner.jpg`, { type: "image/jpeg" });
}

/**
 * Upload banner first onto mission.image, save, then link extra documents.
 */
export async function saveMissionWithDocuments(
  missionData,
  idToken,
  missionId = null,
  options = {},
) {
  const {
    pendingFiles = [],
    pendingBannerFile = null,
    onFileProgress,
  } = options;

  const payload = { ...missionData };
  if (pendingBannerFile) {
    const uploaded = await uploadFile(pendingBannerFile, idToken, (percent) =>
      onFileProgress?.(pendingBannerFile.name, percent),
    );
    const fileName = String(
      uploaded.fileName || uploaded.fileKey || "",
    ).trim();
    if (!fileName) {
      throw new Error("Banner upload succeeded but file path was missing.");
    }
    payload.image = {
      fileName,
      bucket: MISSION_IMAGE_BUCKET,
    };
  } else if (payload.image && typeof payload.image === "object") {
    const fileName = String(
      payload.image.fileName || payload.image.fileKey || "",
    ).trim();
    if (fileName) {
      payload.image = {
        fileName,
        bucket:
          String(payload.image.bucket || "").trim() || MISSION_IMAGE_BUCKET,
      };
    }
  }

  const saveResult = await saveMission(payload, idToken, missionId);
  const resolvedMissionId =
    missionId ||
    saveResult?.missionId ||
    saveResult?.data?.missionId;

  if (pendingFiles.length > 0) {
    if (!resolvedMissionId) {
      throw new Error(
        "Mission was saved but no missionId was returned; cannot link documents.",
      );
    }
    await uploadAndLinkMissionDocuments(
      String(resolvedMissionId),
      pendingFiles,
      idToken,
      { onFileProgress },
    );
  }

  return saveResult;
}

/** Fixed S3 bucket for resource-request images (imageFile.bucket is required). */
export const RESOURCE_REQUEST_IMAGE_BUCKET = "info.avipair.com";

/**
 * Upload one image, then save resource request with input.image (imageFile).
 */
export async function saveResourceRequestWithImage(
  requestData,
  idToken,
  requestId = null,
  options = {},
) {
  const { pendingImageFiles = [], onFileProgress } = options;
  const payload = { ...requestData };

  const imageFile =
    pendingImageFiles.find((f) => f?.type?.startsWith("image/")) ??
    pendingImageFiles[0];

  if (imageFile) {
    const uploaded = await uploadFile(imageFile, idToken, (percent) =>
      onFileProgress?.(imageFile.name, percent),
    );
    const fileName = String(
      uploaded.fileName || uploaded.fileKey || "",
    ).trim();
    if (!fileName) {
      throw new Error("Image upload succeeded but file path was missing.");
    }
    payload.image = {
      fileName,
      bucket: RESOURCE_REQUEST_IMAGE_BUCKET,
    };
  } else if (payload.image && typeof payload.image === "object") {
    const fileName = String(
      payload.image.fileName || payload.image.fileKey || "",
    ).trim();
    if (fileName) {
      payload.image = {
        fileName,
        bucket:
          String(payload.image.bucket || "").trim() ||
          RESOURCE_REQUEST_IMAGE_BUCKET,
      };
    }
  }

  return saveResourceRequest(payload, idToken, requestId);
}
